import React, { useEffect, useState, useMemo } from "react";
import {
  Box, Button, Typography, Grid, Chip, TextField, Select, MenuItem,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  CircularProgress, InputAdornment, IconButton, Tooltip,
} from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { useNavigate } from "react-router-dom";
import TimelineIcon from "@mui/icons-material/Timeline";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import InventoryIcon from "@mui/icons-material/Inventory";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

const API_URL = (process.env.REACT_APP_API_URL || "").trim();

const TYPE_LABELS: Record<string, { label: string; color: string; bg: string }> = {
  procurement: { label: "Received", color: "#059669", bg: "#F0FDF4" },
  manual_add: { label: "Manual Add", color: "#2563EB", bg: "#EFF6FF" },
  manual_deduct: { label: "Manual Deduct", color: "#DC2626", bg: "#FEF2F2" },
  adjustment: { label: "Adjustment", color: "#D97706", bg: "#FFFBEB" },
  order_deduct: { label: "Order", color: "#7C3AED", bg: "#F5F3FF" },
};

const REASON_LABELS: Record<string, string> = {
  damaged: "Damaged",
  expired: "Expired",
  theft: "Theft/Loss",
  count_correction: "Count Correction",
  returned: "Returned",
  other: "Other",
};

const InventoryMovements: React.FC = () => {
  const navigate = useNavigate();
  const [movements, setMovements] = useState<any[]>([]);
  const [summary, setSummary] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [reasonFilter, setReasonFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const getHeaders = () => {
    const t = localStorage.getItem("token") || "";
    return { "Content-Type": "application/json", Accept: "application/json", Authorization: `Bearer ${t}` };
  };

  const loadMovements = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("limit", "200");
      const res = await fetch(`${API_URL}/warehouse/movements?${params}`, { headers: getHeaders() });
      const json = await res.json();
      if (json.success) {
        setMovements(json.data || []);
        setSummary(json.summary || null);
      }
    } catch {} finally { setLoading(false); }
  };

  useEffect(() => { loadMovements(); }, []);

  // Client-side filter pipeline — search + type + reason + date range
  const filteredMovements = useMemo(() => {
    const term = (search || "").trim().toLowerCase();
    const fromTs = dateFrom ? new Date(dateFrom + "T00:00:00").getTime() : null;
    const toTs = dateTo ? new Date(dateTo + "T23:59:59").getTime() : null;
    return movements.filter((m: any) => {
      if (typeFilter && m.type !== typeFilter) return false;
      if (reasonFilter && m.reason !== reasonFilter) return false;
      if (fromTs || toTs) {
        const ts = m.created_at ? new Date(m.created_at).getTime() : 0;
        if (fromTs && ts < fromTs) return false;
        if (toTs && ts > toTs) return false;
      }
      if (term) {
        const haystack = [
          m.product?.productName,
          m.reference,
          m.notes,
          m.product_id != null ? `#${m.product_id}` : "",
        ].join(" ").toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      return true;
    });
  }, [movements, search, typeFilter, reasonFilter, dateFrom, dateTo]);

  const resetFilters = () => {
    setSearch("");
    setTypeFilter("");
    setReasonFilter("");
    setDateFrom("");
    setDateTo("");
  };

  const hasActiveFilters = !!(search || typeFilter || reasonFilter || dateFrom || dateTo);

  return (
    <Box sx={{ background: "linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)", minHeight: "100vh" }}>
      {/* Header */}
      <Box sx={{ bgcolor: "transparent", px: 3, pt: 3, pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box sx={{
            width: 46, height: 46, borderRadius: "12px",
            background: "linear-gradient(135deg, #0f172a 0%, #1e293b 100%)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <TimelineIcon sx={{ fontSize: 22, color: "#fff" }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography fontSize={22} fontWeight={700} color="#0f172a" letterSpacing="-0.02em">Stock Movements</Typography>
            <Typography fontSize={13} color="#64748b">Full audit trail of all inventory changes</Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ p: 3 }}>
        {/* KPI Cards */}
        {summary && (
          <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
            {[
              { label: "Total Movements", value: summary.total_movements?.toLocaleString(), color: "#0f172a", accent: "#0f172a" },
              { label: "Total Stock In",  value: `+${Number(summary.total_in || 0).toLocaleString()}`,  color: "#16a34a", accent: "#16a34a" },
              { label: "Total Stock Out", value: `-${Number(summary.total_out || 0).toLocaleString()}`, color: "#dc2626", accent: "#dc2626" },
              { label: "Adjustments Today", value: summary.adjustments_today, color: "#f59e0b", accent: "#f59e0b" },
            ].map((kpi, i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Box sx={{
                  bgcolor: "#ffffff",
                  borderRadius: "16px",
                  border: "1px solid #e2e8f0",
                  borderLeft: `4px solid ${kpi.accent}`,
                  p: 2.5,
                  transition: "all 0.2s ease",
                  "&:hover": { transform: "translateY(-2px)", borderColor: "#0f172a" },
                }}>
                  <Typography fontSize={11} color="#64748b" fontWeight={600} textTransform="uppercase" letterSpacing="0.06em">{kpi.label}</Typography>
                  <Typography fontSize={26} fontWeight={700} color={kpi.color} letterSpacing="-0.02em" mt={0.75}>{kpi.value}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Filters */}
        <Box sx={{
          display: "flex", gap: 1.5, mb: 2.5, flexWrap: "wrap", alignItems: "center",
          bgcolor: "#ffffff", borderRadius: "14px", border: "1px solid #e5e7eb", p: 2,
        }}>
          <TextField size="small" placeholder="Search product, reference, notes..."
            value={search} onChange={e => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: "#9CA3AF" }} /></InputAdornment> }}
            sx={{ minWidth: 300, "& .MuiOutlinedInput-root": { height: 42, borderRadius: "10px", fontSize: 13, bgcolor: "#fff", "& fieldset": { borderColor: "#e5e7eb" }, "&:hover fieldset": { borderColor: "#cbd5e1" }, "&.Mui-focused fieldset": { borderColor: "#1e293b", borderWidth: "1.5px" } } }} />
          <Select size="small" displayEmpty value={typeFilter} onChange={e => setTypeFilter(e.target.value)}
            sx={{ minWidth: 150, height: 42, borderRadius: "10px", fontSize: 13, bgcolor: "#fff", "& fieldset": { borderColor: "#e5e7eb" }, "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" }, "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#1e293b", borderWidth: "1.5px" } }}>
            <MenuItem value="">All Types</MenuItem>
            {Object.entries(TYPE_LABELS).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
          </Select>
          <Select size="small" displayEmpty value={reasonFilter} onChange={e => setReasonFilter(e.target.value)}
            sx={{ minWidth: 150, height: 42, borderRadius: "10px", fontSize: 13, bgcolor: "#fff", "& fieldset": { borderColor: "#e5e7eb" }, "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#cbd5e1" }, "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#1e293b", borderWidth: "1.5px" } }}>
            <MenuItem value="">All Reasons</MenuItem>
            {Object.entries(REASON_LABELS).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
          </Select>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            {(["From", "To"] as const).map((label) => {
              const value = label === "From" ? dateFrom : dateTo;
              const setValue = label === "From" ? setDateFrom : setDateTo;
              return (
                <DatePicker
                  key={label}
                  label={label}
                  value={value ? dayjs(value) : null}
                  onChange={(d: Dayjs | null) => setValue(d ? d.format("YYYY-MM-DD") : "")}
                  format="MMM D, YYYY"
                  slotProps={{
                    textField: {
                      size: "small",
                      InputLabelProps: { shrink: true },
                      sx: {
                        width: 175,
                        "& .MuiInputLabel-root.Mui-focused": { color: "#0f172a" },
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "12px",
                          fontSize: 13,
                          bgcolor: "#F8FAFC",
                          transition: "all 0.18s ease",
                          "& fieldset": { borderColor: "#E5E7EB", transition: "all 0.18s ease" },
                          "&:hover fieldset": { borderColor: "#cbd5e1" },
                          "&.Mui-focused fieldset": { borderColor: "#0f172a", borderWidth: "1.5px" },
                          "& input": {
                            fontWeight: value ? 600 : 400,
                            color: value ? "#0f172a" : undefined,
                          },
                        },
                      },
                    },
                    desktopPaper: {
                      sx: {
                        mt: 1,
                        borderRadius: "16px",
                        border: "1px solid #e2e8f0",
                        boxShadow: "0 12px 28px rgba(15,23,42,0.08), 0 2px 6px rgba(15,23,42,0.04)",
                        overflow: "hidden",
                        // Header bar (month + nav)
                        "& .MuiPickersCalendarHeader-root": {
                          bgcolor: "#0f172a",
                          color: "#fff",
                          margin: 0,
                          px: 2,
                          py: 1.25,
                          minHeight: 48,
                          maxHeight: 48,
                        },
                        "& .MuiPickersCalendarHeader-label": {
                          fontSize: 14,
                          fontWeight: 600,
                          color: "#fff",
                        },
                        "& .MuiPickersCalendarHeader-switchViewIcon": { color: "#fff" },
                        "& .MuiPickersArrowSwitcher-root .MuiIconButton-root": {
                          color: "#fff",
                          borderRadius: "8px",
                          "&:hover": { bgcolor: "rgba(255,255,255,0.12)" },
                        },
                        // Body padding
                        "& .MuiDayCalendar-header, & .MuiDayCalendar-monthContainer, & .MuiDayCalendar-weekContainer": {
                          paddingLeft: "8px",
                          paddingRight: "8px",
                        },
                        "& .MuiDayCalendar-header": { mt: 1 },
                        // Week labels
                        "& .MuiDayCalendar-weekDayLabel": {
                          fontSize: 11.5,
                          fontWeight: 600,
                          color: "#94a3b8",
                          width: 36,
                          height: 32,
                        },
                        // Day cells
                        "& .MuiPickersDay-root": {
                          width: 36,
                          height: 36,
                          fontSize: 13,
                          borderRadius: "10px",
                          color: "#334155",
                          margin: "2px",
                          transition: "all 0.15s ease",
                          "&:hover": { bgcolor: "#f1f5f9" },
                          "&.Mui-disabled": { color: "#cbd5f5" },
                        },
                        "& .MuiPickersDay-today:not(.Mui-selected)": {
                          border: "1px solid #0f172a",
                          bgcolor: "transparent",
                        },
                        "& .MuiPickersDay-root.Mui-selected": {
                          bgcolor: "#0f172a !important",
                          color: "#fff !important",
                          fontWeight: 600,
                          "&:hover": { bgcolor: "#1e293b !important" },
                        },
                        // Action bar (Today / Clear)
                        "& .MuiDialogActions-root": {
                          padding: "8px 12px",
                          borderTop: "1px solid #f1f5f9",
                          "& .MuiButton-root": {
                            color: "#0f172a",
                            fontWeight: 600,
                            textTransform: "none",
                            fontSize: 13,
                            "&:hover": { bgcolor: "transparent", textDecoration: "underline" },
                          },
                        },
                      },
                    },
                    actionBar: { actions: ["today", "clear"] },
                  }}
                />
              );
            })}
          </LocalizationProvider>
          {hasActiveFilters && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<RestartAltIcon sx={{ fontSize: 16 }} />}
              onClick={resetFilters}
              sx={{
                borderRadius: "10px", textTransform: "none", fontSize: 12, fontWeight: 600,
                borderColor: "#E5E7EB", color: "#0f172a", px: 2, height: 40,
                "&:hover": { borderColor: "#0f172a", bgcolor: "#f8fafc" },
              }}
            >
              Reset Filters
            </Button>
          )}
          {hasActiveFilters && (
            <Typography sx={{ fontSize: 12, color: "#64748B", ml: 0.5 }}>
              Showing {filteredMovements.length} of {movements.length}
            </Typography>
          )}
        </Box>

        {/* Movements Table */}
        <Box sx={{ bgcolor: "#ffffff", borderRadius: "16px", border: "1px solid #e2e8f0", overflow: "hidden" }}>
          {loading ? (
            <Box sx={{ textAlign: "center", py: 8 }}><CircularProgress size={28} sx={{ color: "#0f172a" }} /></Box>
          ) : (
            <TableContainer>
              <Table size="small" sx={{
                "& .MuiTableHead-root .MuiTableRow-root": {
                  background: "linear-gradient(90deg, #0f172a, #1e293b)",
                },
                "& .MuiTableHead-root .MuiTableCell-root": {
                  background: "linear-gradient(90deg, #0f172a, #1e293b)",
                  backgroundImage: "linear-gradient(90deg, #0f172a, #1e293b)",
                  color: "#ffffff",
                  fontSize: 11,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  borderBottom: "none",
                  py: 1.75,
                },
                "& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even) .MuiTableCell-root": {
                  bgcolor: "#f8fafc",
                },
                "& .MuiTableBody-root .MuiTableRow-root:hover .MuiTableCell-root": {
                  bgcolor: "#eef2ff !important",
                },
              }}>
                <TableHead>
                  <TableRow>
                    {["Date & Time", "Product", "Type", "Qty", "Stock Before", "Stock After", "Reference", "Reason", "Notes"].map(h => (
                      <TableCell key={h}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredMovements.map((m: any) => {
                    const t = String(m.type || "").toLowerCase();
                    const pillColor =
                      t === "procurement" ? { c: "#16a34a", bg: "#dcfce7" }
                      : t === "manual_add" ? { c: "#2563eb", bg: "#dbeafe" }
                      : t === "manual_deduct" ? { c: "#dc2626", bg: "#fee2e2" }
                      : t === "adjustment" ? { c: "#f59e0b", bg: "#fef3c7" }
                      : t === "order_deduct" ? { c: "#7c3aed", bg: "#ede9fe" }
                      : { c: "#475569", bg: "#f1f5f9" };
                    const tInfo = TYPE_LABELS[m.type] || { label: m.type };
                    const isPositive = m.qty > 0;
                    return (
                      <TableRow key={m.id} sx={{ height: 56, cursor: "pointer", transition: "background 0.15s" }}>
                        <TableCell sx={{ fontSize: 12.5, color: "#334155", whiteSpace: "nowrap" }}>
                          {new Date(m.created_at).toLocaleDateString()}{" "}
                          <Typography component="span" fontSize={10.5} color="#94a3b8">{new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography fontSize={13} fontWeight={600} color="#0f172a"
                            sx={{ cursor: "pointer", "&:hover": { color: "#1e293b", textDecoration: "underline" } }}
                            onClick={() => m.product_id && navigate(`/product-history/${m.product_id}`)}>
                            {m.product?.productName || `#${m.product_id}`}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box component="span" sx={{
                            display: "inline-block",
                            px: "10px", py: "4px",
                            borderRadius: "999px",
                            fontSize: 12,
                            fontWeight: 600,
                            bgcolor: pillColor.bg,
                            color: pillColor.c,
                          }}>
                            {tInfo.label}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            {isPositive ? <ArrowUpwardIcon sx={{ fontSize: 14, color: "#16a34a" }} /> : <ArrowDownwardIcon sx={{ fontSize: 14, color: "#dc2626" }} />}
                            <Typography fontSize={13.5} fontWeight={600} color={isPositive ? "#16a34a" : "#dc2626"}>
                              {isPositive ? "+" : ""}{Number(m.qty).toLocaleString()}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontSize: 12.5, color: "#64748b", fontFamily: "monospace" }}>{Number(m.stock_before).toLocaleString()}</TableCell>
                        <TableCell sx={{ fontSize: 12.5, color: "#0f172a", fontWeight: 600, fontFamily: "monospace" }}>{Number(m.stock_after).toLocaleString()}</TableCell>
                        <TableCell>
                          {m.reference ? (
                            <Tooltip title="View source">
                              <Typography fontSize={11.5} fontFamily="monospace"
                                sx={{
                                  color: "#2563eb",
                                  fontWeight: 500,
                                  cursor: m.reference?.includes("PO") ? "pointer" : "default",
                                  "&:hover": { textDecoration: m.reference?.includes("PO") ? "underline" : "none" },
                                }}
                                onClick={() => {
                                  const poMatch = m.reference?.match(/PO\s*#?\s*(\d+)/i);
                                  if (poMatch) navigate(`/po-detail/${poMatch[1]}`);
                                }}>
                                {m.reference}
                              </Typography>
                            </Tooltip>
                          ) : <Typography fontSize={11.5} color="#cbd5e1">—</Typography>}
                        </TableCell>
                        <TableCell>
                          {m.reason ? (
                            <Chip label={REASON_LABELS[m.reason] || m.reason} size="small" variant="outlined"
                              sx={{ height: 22, fontSize: 11, fontWeight: 500, borderColor: "#e2e8f0", color: "#475569", borderRadius: "999px" }} />
                          ) : <Typography fontSize={11.5} color="#cbd5e1">—</Typography>}
                        </TableCell>
                        <TableCell sx={{ fontSize: 11.5, color: "#64748b", maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          <Tooltip title={m.notes || ""}><span>{m.notes || "—"}</span></Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {filteredMovements.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={9} sx={{ textAlign: "center", py: 8 }}>
                        <InventoryIcon sx={{ fontSize: 40, color: "#E5E7EB", mb: 1 }} />
                        <Typography fontSize={14} color="#9CA3AF">{hasActiveFilters ? "No movements match your filters" : "No stock movements found"}</Typography>
                        <Typography fontSize={12} color="#D1D5DB">{hasActiveFilters ? "Try adjusting or resetting filters" : "Movements will appear here as stock changes occur"}</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default InventoryMovements;
